import { CorePaymentInput } from '@components'
import { useHeaderContext } from '@context/HeaderContext'
import { useAppDispatch } from '@hooks'
import settings from '@settings'
import styles from '@withdrawals/styles/WithdrawInitiate.module.scss'
import {
  selectMerchantCurrency,
  setStep,
  useAppSelector,
  useInitializeCardOperations,
  useWithdrawAccounts,
  WithdrawalSubFlows
} from 'mmfintech-backend-api'
import type { AccountBalanceResponse } from 'mmfintech-backend-api/types'
import { GlobalContext, isValidArray, tr, translateError } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useContext, useEffect, useRef, useState } from 'react'

const PayToCard = () => {
  const { currentStep, setCurrentStep } = useHeaderContext()
  const { iframeMessage, resetIframeMessage } = useContext(GlobalContext) as any

  const primaryCurrency = useAppSelector(selectMerchantCurrency)

  const iframeRef = useRef<any>(null)

  const [sourceAccount, setSourceAccount] = useState<AccountBalanceResponse>(null)

  const dispatch = useAppDispatch()

  const [amount, setAmount] = useState('')
  const {
    accounts: activeAccounts,
    accountsError,
    accountsFetching
  } = useWithdrawAccounts({ flowType: WithdrawalSubFlows.PAY_TO_CARD })

  const initializeCardOperations = useInitializeCardOperations()

  useEffect(() => {
    if (iframeRef.current && amount && sourceAccount?.id) {
      iframeRef?.current?.contentWindow?.postMessage(
        {
          type: 'WITHDRAWAL_REQUEST',
          value: {
            amount: Number(amount),
            selectedAccountId: sourceAccount?.id
          }
        },
        '*'
      )
    }
  }, [amount, sourceAccount])

  useEffect(() => {
    if (iframeMessage === 'CARD_WITHDRAWAL_PREVIEW') {
      setCurrentStep(2)
      dispatch(setStep(2))
      resetIframeMessage()
    }
    if (iframeMessage === 'CARD_WITHDRAWAL_BACK') {
      setCurrentStep(1)
      dispatch(setStep(1))
      resetIframeMessage()
    }
    if (iframeMessage === 'CHALLENGE_SOLVED') {
    }
  }, [iframeMessage])

  // biome-ignore lint/correctness/useExhaustiveDependencies: sourceAccount intentionally omitted — re-running when it changes would create a loop
  useEffect(() => {
    if (!isValidArray(activeAccounts) || sourceAccount) return

    if (activeAccounts.length === 1) {
      setSourceAccount(activeAccounts[0])
      return
    }

    if (!primaryCurrency) return

    const primaryAccount = activeAccounts.find(
      account => account.type === 'PRIMARY' && account.currencyCode === primaryCurrency
    )
    if (primaryAccount) setSourceAccount(primaryAccount)
  }, [activeAccounts, primaryCurrency])

  return (
    <div className={styles['send-money-container']} data-test='pay-to-card-container'>
      {currentStep === 1 && (
        <div className='from-container'>
          <div className='from-to-label-container'>
            <div className='from-to-label'>{tr('FRONTEND.WITHDRAW.PAY_TO_CARD.FROM', 'From')}</div>
          </div>
          <CorePaymentInput
            selectedOption={sourceAccount}
            setSelectedAccount={setSourceAccount}
            dataTest={'deposit-payment-input-test'}
            options={activeAccounts}
            paymentOption={isValidArray(sourceAccount?.paymentOptions) && sourceAccount?.paymentOptions[0]}
            maxInputAmount={sourceAccount?.availableBalance}
            enableMaxAmountButton
            onChange={value => setAmount(value)}
            value={amount}
            error={translateError(accountsError)}
            isLoading={accountsFetching}
          />
        </div>
      )}

      <ErrorDisplay error={accountsError} />

      <iframe
        title={tr('FRONTEND.PAY_TO_CARD.PAYTOCARD', 'PayToCard')}
        src={`${settings.cardsUrl}withdrawal`}
        allow='clipboard-read; clipboard-write'
        ref={iframeRef}
        onLoad={async ev => {
          const frame = ev?.target as any

          initializeCardOperations((token: string) => {
            frame.contentWindow.postMessage({ type: 'HANDSHAKE', value: token }, '*')
            frame.contentWindow.postMessage(
              {
                type: 'WITHDRAWAL_REQUEST',
                value: {
                  amount: Number(amount),
                  selectedAccountId: sourceAccount?.id
                }
              },
              '*'
            )
          })
        }}
      />
    </div>
  )
}

export default PayToCard
